// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// 11 §6's report control (M9-28).
//
// **The server has answered this since M9-28 and no client asked.** `machines.report` exists, is
// tested, has an OpenAPI path and a fixture — and `grep -rn 'report' src/` returned nothing that
// called it. So the moderation loop had one entrance (a first publication) and no other: a player
// who met something on a playfield that should not be there had nowhere to say so, and
// `reviewDecision`'s `'reported'` reason could not fire for any machine whose author had stopped
// publishing. That is the sixth time this repository has recorded a finished feature wired to
// nothing, and the fix is a caller, not more server.
//
// **A closed list of five reasons and a box for the words.** `REPORT_REASONS` is short and closed
// for a reason `moderation.ts` states: a free-text reason is a field nobody can count, and counting
// is what turns twenty reports into a queue position. So the reason is chosen and the detail is
// typed, and only the first of the two is a number.
//
// **It never says "you have already reported this."** The table holds one per player per version
// and the handler swallows the duplicate; a second press is answered exactly like the first. Saying
// otherwise is an invitation to report again from another account, and this panel is the surface
// that would be doing the inviting.
//
// **Only for machines somebody wrote.** `meta.author.kind === 'first-party'` is not offered the
// control at all: reporting NEON NOVA to the people who made NEON NOVA is a support email wearing a
// moderation form's clothes, and a control that cannot usefully act must not be offered — the
// M8-63 rule, learned from a wrist menu that offered End game over a machine no longer in the room.

import { useCallback, useState } from 'react'
import { useLocalisation } from '../i18n/useLanguage'
import type { MessageKey } from '../i18n'

/**
 * The five reasons, and their labels.
 *
 * Kept beside the ids rather than derived from them, so a reason the server adds is a compile error
 * here rather than a blank radio button. `REPORT_REASONS` in `server/ts/moderation.ts` is the list;
 * `test/client/report-machine.test.ts` holds the two to each other.
 */
export const REPORT_REASON_LABELS: readonly { readonly id: string; readonly label: MessageKey }[] = [
  { id: 'offensive-text', label: 'report.reason.offensive-text' },
  { id: 'real-machine', label: 'report.reason.real-machine' },
  { id: 'broken', label: 'report.reason.broken' },
  { id: 'misleading-rating', label: 'report.reason.misleading-rating' },
  { id: 'other', label: 'report.reason.other' },
]

export interface ReportMachineProps {
  readonly machineTitle: string
  /** Sends it. Resolves with what to say, never throws. */
  readonly onSend: (reason: string, detail: string) => Promise<'sent' | 'needs-account' | 'offline' | 'failed'>
  readonly onClose: () => void
}

export function ReportMachine({
  machineTitle,
  onSend,
  onClose,
}: ReportMachineProps): React.JSX.Element {
  const { t } = useLocalisation()
  const [reason, setReason] = useState(REPORT_REASON_LABELS[0]?.id ?? 'other')
  const [detail, setDetail] = useState('')
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState<MessageKey | undefined>(undefined)

  const send = useCallback(async () => {
    setBusy(true)
    const outcome = await onSend(reason, detail)
    setBusy(false)
    setSaid(
      outcome === 'sent'
        ? 'report.sent'
        : outcome === 'needs-account'
          ? 'report.needs-account'
          : outcome === 'offline'
            ? 'error.offline'
            : 'report.failed',
    )
  }, [onSend, reason, detail])

  return (
    <div className="report" role="dialog" aria-label={t('report.heading')}>
      <h2 className="report__heading">{t('report.heading')}</h2>
      <p className="muted">{t('report.about', { title: machineTitle })}</p>

      <fieldset className="report__reasons">
        <legend>{t('report.reason-legend')}</legend>
        {REPORT_REASON_LABELS.map((choice) => (
          <label className="report__reason" key={choice.id} htmlFor={`report-${choice.id}`}>
            <input
              id={`report-${choice.id}`}
              type="radio"
              name="report-reason"
              title={t(choice.label)}
              value={choice.id}
              checked={reason === choice.id}
              onChange={() => setReason(choice.id)}
            />
            {t(choice.label)}
          </label>
        ))}
      </fieldset>

      <label className="field" htmlFor="report-detail">
        {t('report.detail')}
      </label>
      <input
        id="report-detail"
        type="text"
        className="field__input"
        title={t('report.detail-title')}
        maxLength={400}
        value={detail}
        onChange={(event) => setDetail(event.target.value)}
      />

      {said === undefined ? null : (
        <p className="notice" role="status" aria-live="polite">
          {t(said)}
        </p>
      )}

      <div className="report__actions">
        {/*
          Sending is disabled once it has been sent. Not because a second one would be counted —
          the unique constraint sees to that — but because a button that stays live after "Thank
          you" invites somebody to press it until something changes, and nothing will.
        */}
        <button
          type="button"
          className="button button--primary"
          title={t('report.send-title')}
          disabled={busy || said === 'report.sent'}
          onClick={() => void send()}
        >
          {t('report.send')}
        </button>
        <button
          type="button"
          className="button button--quiet"
          title={t('report.close-title')}
          onClick={onClose}
        >
          {t('report.close')}
        </button>
      </div>
    </div>
  )
}

/**
 * Is this a machine a player can meaningfully report?
 *
 * A first-party machine is not: the report goes to the people who wrote it, which is a support
 * email rather than a moderation queue item. Exported so `MachineStage` and a test ask the same
 * question of the same bytes.
 */
export function isReportable(definition: unknown): boolean {
  const meta = (definition as { meta?: { author?: { kind?: unknown } } } | null)?.meta
  return meta?.author?.kind !== 'first-party'
}
